import React from 'react'
// libs
import styled from 'styled-components'
// components
import { Paper } from './components/LinkedItem/view'
// constants
import { ENTITIES } from 'constants/index'
import { LINK_WEIGHTING_OPTIONS } from 'components/Modals/PlanItems/components/PlanLinks/components/LinkedItem'
// components
import { LinkedItemContainer } from 'containers/LinkedItems'
// views
import { Collapse } from 'components/Modals/PlanItems/styles'
// utils
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'
import { getEntityPath } from 'utils/plan-tree'
import { getOptionByValue } from 'utils/form'
// types
import { DocumentNode } from 'graphql/language'
import { IPlanAction, IPlanKpi, IPlanObjective } from 'types/graphql'

// styled
const Wrapper = styled('div')`
  padding-bottom: 16px;

  ${Paper} {
    margin: 16px 16px 0 16px;
  }
`

// types
interface IIncomingProps {
  disabled: boolean
  entity: IPlanObjective | IPlanAction | IPlanKpi
  query: DocumentNode
  variables: any
}

const PlanLinks: React.FunctionComponent<IIncomingProps> = ({ disabled, entity, query, variables }) => {
  // state
  const [isCollapsed, setCollapsed] = React.useState(true)

  // constants
  const sourceLinkingItems = get(entity, 'sourceLinkingItems', [])
  const destinationLinkingItems = get(entity, 'destinationLinkingItems', [])
  const linkingItems = orderBy([...destinationLinkingItems, ...sourceLinkingItems], ['createdAt'], ['asc'])

  React.useEffect(() => {
    setCollapsed(linkingItems.length === 0)
  }, [linkingItems.length])

  // methods
  const generateSourceItem = (source, type, isDestination = false) => {
    let data

    if (type === ENTITIES.PLAN_ACTION) {
      if (isDestination) {
        data = source.destinationPlanAction
      } else {
        data = source.sourcePlanAction
      }
    }

    if (type === ENTITIES.PLAN_KPI) {
      if (isDestination) {
        data = source.destinationPlanKPI
      } else {
        data = source.sourcePlanKPI
      }
    }

    if (type === ENTITIES.PLAN_OBJECTIVE) {
      if (isDestination) {
        data = source.destinationPlanObjective
      } else {
        data = source.sourcePlanObjective
      }
    }

    return {
      ...data,
      type,
      linkItemId: source.id,
      comment: source.description,
      weighting: source.weighting,
      parentId: source.parentId,
      path: getEntityPath(data)
    }
  }

  const getSource = (linkItem, entity) => {
    if (linkItem.sourcePlanAction && linkItem.sourcePlanActionId !== entity.id) {
      return generateSourceItem(linkItem, ENTITIES.PLAN_ACTION)
    }

    if (linkItem.sourcePlanKPI && linkItem.sourcePlanKPIId !== entity.id) {
      return generateSourceItem(linkItem, ENTITIES.PLAN_KPI)
    }

    if (linkItem.sourcePlanObjective && linkItem.sourcePlanObjectiveId !== entity.id) {
      return generateSourceItem(linkItem, ENTITIES.PLAN_OBJECTIVE)
    }

    if (linkItem.destinationPlanAction && linkItem.destinationPlanActionId !== entity.id) {
      return generateSourceItem(linkItem, ENTITIES.PLAN_ACTION, true)
    }

    if (linkItem.destinationPlanKPI && linkItem.destinationPlanKPIId !== entity.id) {
      return generateSourceItem(linkItem, ENTITIES.PLAN_KPI, true)
    }

    if (linkItem.destinationPlanObjective && linkItem.destinationPlanObjectiveId !== entity.id) {
      return generateSourceItem(linkItem, ENTITIES.PLAN_OBJECTIVE, true)
    }
  }

  return (
    <Wrapper>
      <Collapse handleClick={() => setCollapsed(!isCollapsed)} isCollapsed={isCollapsed}>
        {`Plan Links (${linkingItems.length})`}
      </Collapse>
      {!isCollapsed &&
        linkingItems.map(linkItem => {
          const source = getSource(linkItem, entity)

          if (!source) return

          const initialValues = {
            comment: get(source, 'comment', ''),
            weighting: getOptionByValue(get(source, 'weighting'), LINK_WEIGHTING_OPTIONS),
            parent: getOptionByValue(get(source, 'parentId'), [
              {
                label: source.description,
                value: source.id
              },
              { label: entity.description, value: entity.id }
            ])
          }

          return (
            <LinkedItemContainer
              form={`LINKED_ITEM_COMMENT_FORM_${source.id}`}
              initialValues={initialValues}
              key={source.id}
              entity={entity}
              query={query}
              source={source}
              variables={variables}
              disabled={disabled}
            />
          )
        })}
    </Wrapper>
  )
}

export default PlanLinks
